import React from "react";
import { cva, type VariantProps } from "cva";
import { LinearProgress } from "@mui/material";

const buttonVariants = cva(
    "relative isolate inline-flex items-center justify-center gap-2 overflow-hidden whitespace-nowrap rounded-md border-[1px] border-solid text-sm font-medium leading-none ring-offset-background transition-[background-color,border-color,color,box-shadow,transform,opacity] duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 active:translate-y-px active:scale-[0.99] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:transition-none motion-reduce:active:translate-y-0 motion-reduce:active:scale-100",
    {
        variants: {
            variant: {
                default:
                    "border-transparent bg-[#7556FA] text-white shadow-[0_1px_2px_rgba(60,55,105,0.12),0_8px_18px_rgba(117,86,250,0.18)] hover:bg-[#684BEA] hover:shadow-[0_2px_4px_rgba(60,55,105,0.14),0_10px_22px_rgba(117,86,250,0.22)] focus-visible:ring-[#7556FA]/30",
                outline:
                    "border-[#dee2e6] bg-white text-[#7556FA] shadow-[0_1px_2px_rgba(60,55,105,0.06)] hover:border-[#7556FA] hover:bg-[#F7F5FF] hover:shadow-[0_2px_8px_rgba(60,55,105,0.08)] focus-visible:ring-[#7556FA]/30",
                secondary:
                    "border-[#dee2e6] text-[#3C3769] shadow-[0_1px_2px_rgba(60,55,105,0.05)] hover:border-[#CFE59E] hover:bg-[#ECEDFE] hover:text-[#2F2A57] hover:shadow-[0_2px_8px_rgba(60,55,105,0.08)] focus-visible:ring-[#7556FA]/30",
                error: "border-[#E25C5C] bg-white text-[#E25C5C] shadow-[0_1px_2px_rgba(226,92,92,0.10)] hover:border-[#E25C5C] hover:bg-[#FDECEC] hover:text-[#C94A4A] hover:shadow-[0_2px_8px_rgba(226,92,92,0.16)] focus-visible:ring-[#E25C5C]/30",
            },
            size: {
                sm: "h-8 px-3 text-xs",
                md: "h-9 px-4 text-sm",
                lg: "min-h-[48px] px-8 py-3 text-[16px]",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "md",
        },
    }
);

type UIButtonProps = React.ComponentProps<"button"> &
    VariantProps<typeof buttonVariants> & {
        loading?: boolean;
    };

export default function UIButton({
    className,
    size,
    variant,
    loading,
    disabled,
    children,
    ...props
}: UIButtonProps) {
    return (
        <button
            className={buttonVariants({ variant, size, className })}
            disabled={loading || disabled}
            {...props}
        >
            {children}
            {loading && (
                <span className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit] opacity-25">
                    <LinearProgress
                        color="inherit"
                        sx={{
                            height: "100%",
                            width: "100%",
                            backgroundColor: "transparent",
                            "& .MuiLinearProgress-bar": {
                                backgroundColor: "currentColor",
                            },
                        }}
                    />
                </span>
            )}
        </button>
    );
}
